import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  ActionButton,
  Buttons,
  CancelButton,
  DayPickerInput,
  Drawer,
  HelpTooltip,
  SingleSelect,
  Table,
  TextInput,
} from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { dateFormats } from '~/utils';
import { PageLoader } from '~/routes/public/pages';
import AccountSelect from './components/AccountSelect';
import InvoiceItemSelect from './components/InvoiceItemSelect';
import ItemSelect from './components/ItemSelect';
import TaxCodeSelect from './components/TaxCodeSelect';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

const CustomContainer = styled.div`
  width: 100%;
  input {
    font-size: 0.875rem;
  }
`;

const DatePickerContainer = styled.div`
  width: 100%;
`;

const onlinePaymentOptions = { default: null, yes: true, no: false };
const onlinePaymentValues = Object.fromEntries(Object.entries(onlinePaymentOptions).map(([k, v]) => [v, k]));

export default function ConfigurationDrawer({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { qboConnectionId } = useParams();

  const [configuration, setConfiguration] = useState({ data: null, isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getConfiguration();

      if (!isMounted.current) return;
      setConfiguration({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const [items, setItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getItems();

      if (!isMounted.current) return;
      setItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const [invoiceItems, setInvoiceItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).invoiceItems().get();

      if (!isMounted.current) return;
      setInvoiceItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted]);

  const [taxCodes, setTaxCodes] = useState({ data: [], isReady: false });
  useEffect(() => {
    if (configuration?.data?.region !== 'international') return;

    (async () => {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getTaxCodes();

      if (!isMounted.current) return;
      setTaxCodes({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, configuration?.data?.region, qboConnectionId]);

  const [accounts, setAccounts] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getDepositAccounts();

      if (!isMounted.current) return;
      setAccounts({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const [expenseAccounts, setExpenseAccounts] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getExpenseAccounts();

      if (!isMounted.current) return;
      setExpenseAccounts({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, qboConnectionId]);

  return (
    <Drawer
      isOpen
      title="Configuration"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          if (configuration.data.vendorBillEnabled && !configuration.data.vendorBillSyncStartDate) {
            toast.error('A sync start date is required when vendor bill sync is enabled.');
            return;
          }

          form.submit();

          try {
            await api.www.workspaces(workspace.id).qbo(qboConnectionId).updateConfiguration({
              name: configuration.data.name,
              qboItemId: configuration.data.qboItemId,
              defaultInvoiceItemId: configuration.data.defaultInvoiceItemId,
              qboPaymentDepositAccountId: configuration.data.qboPaymentDepositAccountId,
              qboTaxableTaxCodeId: configuration.data.qboTaxableTaxCodeId,
              qboNonTaxableTaxCodeId: configuration.data.qboNonTaxableTaxCodeId,
              vendorBillEnabled: configuration.data.vendorBillEnabled,
              vendorBillSyncStartDate: configuration.data.vendorBillSyncStartDate,
              defaultExpenseAccountId: configuration.data.defaultExpenseAccountId,
              allowCreditCardPayment: configuration.data.allowCreditCardPayment,
              allowAchPayment: configuration.data.allowAchPayment,
              defaultRemoveFromAccounting: configuration.data.defaultRemoveFromAccounting,
            });
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        const handleChange = (event) => {
          setDirty(true);

          setConfiguration((state) => ({
            ...state,
            data: { ...state.data, [event.target.name]: event.target.value || null },
          }));
        };

        const handleValueChange = (key, value) => {
          setDirty(true);
          setConfiguration((state) => ({
            ...state,
            data: { ...state.data, [key]: value },
          }));
        };

        if (!configuration.isReady || !items.isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Setting</Table.Column>

                <Table.Column>
                  <strong>Value</strong>
                </Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                <Table.BoxRow>
                  <Table.Cell>
                    Integration Name
                    <HelpTooltip
                      message="The name of the QuickBooks integration to be referenced in Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <TextInput
                      container={CustomContainer}
                      name="name"
                      value={configuration.data.name}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Delete/Unpublish Default Behavior
                    <HelpTooltip
                      message="When items are deleted or unpublished in Ruddr, this setting controls the default behavior for how the corresponding items in QuickBooks will be handled."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SingleSelect
                      value={configuration.data.defaultRemoveFromAccounting ? 'remove' : 'keep'}
                      onChange={(event) =>
                        handleValueChange('defaultRemoveFromAccounting', event.target.value === 'remove')
                      }
                      showEmptyOption={false}>
                      <option value="keep">Keep Items in QuickBooks</option>
                      <option value="remove">Remove Items from QuickBooks</option>
                    </SingleSelect>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Ruddr Invoice Item
                    <HelpTooltip
                      message="Ruddr will use this default Invoice Item when updating a Ruddr invoice from QuickBooks if the invoice uses a Product/Service that is not mapped to an Invoice Item in Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <InvoiceItemSelect
                      name="defaultInvoiceItemId"
                      invoiceItems={invoiceItems.data}
                      value={configuration.data.defaultInvoiceItemId}
                      onChange={handleChange}
                      showEmptyOption={false}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default QuickBooks Product/Service
                    <HelpTooltip
                      message="Ruddr will use this default Product/Service when publishing an invoice to QuickBooks if an Invoice Item in Ruddr is not mapped to a Product/Service."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <ItemSelect
                      name="qboItemId"
                      items={items.data}
                      value={configuration.data.qboItemId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Payment Deposit Account
                    <HelpTooltip
                      message="Ruddr will use this Deposit Account when saving a payment to QuickBooks."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <AccountSelect
                      name="qboPaymentDepositAccountId"
                      accounts={accounts.data}
                      value={configuration.data.qboPaymentDepositAccountId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                {configuration.data.region === 'us' && (
                  <>
                    <Table.BoxRow>
                      <Table.Cell>
                        Allow Online Credit Card Payment
                        <HelpTooltip
                          message="When selected yes or no, this will override the QuickBooks settings for new invoices."
                          style={{ marginLeft: '.5rem' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <SingleSelect
                          value={onlinePaymentValues[configuration.data.allowCreditCardPayment]}
                          onChange={(event) => {
                            handleValueChange('allowCreditCardPayment', onlinePaymentOptions[event.target.value]);
                          }}
                          showEmptyOption={false}>
                          <option value="default">Use QuickBooks Settings</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </SingleSelect>
                      </Table.Cell>
                    </Table.BoxRow>

                    <Table.BoxRow>
                      <Table.Cell>
                        Allow Online ACH Payment
                        <HelpTooltip
                          message="When selected yes or no, this will override the QuickBooks settings for new invoices."
                          style={{ marginLeft: '.5rem' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <SingleSelect
                          value={onlinePaymentValues[configuration.data.allowAchPayment]}
                          onChange={(event) => {
                            handleValueChange('allowAchPayment', onlinePaymentOptions[event.target.value]);
                          }}
                          showEmptyOption={false}>
                          <option value="default">Use QuickBooks Settings</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </SingleSelect>
                      </Table.Cell>
                    </Table.BoxRow>
                  </>
                )}

                {configuration?.data?.region === 'international' && taxCodes.isReady && (
                  <>
                    <Table.BoxRow>
                      <Table.Cell>
                        Default Taxable Tax Code
                        <HelpTooltip
                          message="Ruddr will use this Tax Code when publishing an invoice to QuickBooks if an invoice line is taxable but the invoice does not have a Tax Rate set."
                          style={{ marginLeft: '.5rem' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <TaxCodeSelect
                          name="qboTaxableTaxCodeId"
                          taxCodes={taxCodes.data}
                          value={configuration.data.qboTaxableTaxCodeId}
                          onChange={handleChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>

                    <Table.BoxRow>
                      <Table.Cell>
                        Default Non-Taxable Tax Code
                        <HelpTooltip
                          message="Ruddr will use this Tax Code when publishing an invoice to QuickBooks if an invoice line is non-taxable."
                          style={{ marginLeft: '.5rem' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <TaxCodeSelect
                          name="qboNonTaxableTaxCodeId"
                          taxCodes={taxCodes.data}
                          value={configuration.data.qboNonTaxableTaxCodeId}
                          onChange={handleChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  </>
                )}

                <Table.BoxRow>
                  <Table.Cell>
                    Reimbursable Expenses Sync
                    <HelpTooltip
                      message="When enabled, approved reimbursable expenses will be synced to QuickBooks as vendor bills."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SingleSelect
                      value={configuration.data.vendorBillEnabled ? 'yes' : 'no'}
                      onChange={(event) => {
                        const enabled = event.target.value === 'yes';
                        handleValueChange('vendorBillEnabled', enabled);
                        if (enabled && !configuration.data.vendorBillSyncStartDate) {
                          handleValueChange('vendorBillSyncStartDate', moment().format(dateFormats.isoDate));
                        }
                      }}
                      showEmptyOption={false}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </SingleSelect>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Reimbursable Expenses Sync Start Date
                    <HelpTooltip
                      message="Reimbursable expenses dated before this date will not be synced."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <DatePickerContainer>
                      <DayPickerInput
                        value={configuration.data.vendorBillSyncStartDate}
                        placeholder="Sync start date"
                        clearable={false}
                        onChange={(value) => handleValueChange('vendorBillSyncStartDate', value)}
                      />
                    </DatePickerContainer>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default QuickBooks Expense Account
                    <HelpTooltip
                      message="Ruddr will use this default expense account when saving a reimbursable expense to QuickBooks if the expense category in Ruddr is not mapped to an expense account in QuickBooks."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <AccountSelect
                      name="defaultExpenseAccountId"
                      accounts={expenseAccounts.data}
                      value={configuration.data.defaultExpenseAccountId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
